/**
 * MailTransmissionForm
 *
 * Form for mail transmission: recipient + address + mailing class.
 * Works with MailTransmission type from the discriminated union.
 */

import React, { useCallback } from 'react';

import {
  AddressAutocomplete,
  StateCodeSelect,
} from '@/components/ui/address-autocomplete';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { DestinationAddress } from '@/services/api/correspondenceService';

import { FormField } from './FormField';
import { inputErrorStyles, inputStyles, labelStyles } from './styles';
import type { MailingClass, MailTransmissionFormProps } from './types';

const MAILING_OPTIONS: {
  value: MailingClass;
  label: string;
  description: string;
  disabled?: boolean;
}[] = [
  {
    value: 'first_class',
    label: 'First Class',
    description: '3-5 business days',
  },
  {
    value: 'certified',
    label: 'Certified',
    description: 'Temporarily unavailable',
    disabled: true,
  },
  { value: 'express', label: 'Express', description: '1-3 business days' },
];

export const MailTransmissionForm: React.FC<MailTransmissionFormProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  errors,
}) => {
  const address = value.address || {
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
  };

  const handleAddressField = useCallback(
    (field: keyof DestinationAddress, fieldValue: string) => {
      onChange({
        ...value,
        address: { ...address, [field]: fieldValue } as DestinationAddress,
      });
    },
    [value, address, onChange],
  );

  const handleAddressAutocomplete = useCallback(
    (autocompleteAddress: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
    }) => {
      onChange({
        ...value,
        address: {
          address_line1: autocompleteAddress.address_line1,
          address_line2: autocompleteAddress.address_line2 || '',
          city: autocompleteAddress.city,
          state: autocompleteAddress.state,
          zip_code: autocompleteAddress.postal_code,
          country: 'US',
        },
      });
    },
    [value, onChange],
  );

  const selectedClass = value.mailing_class || 'first_class';

  return (
    <div className={cn('space-y-2.5', className)}>
      <FormField
        label="Recipient"
        htmlFor="mail-displayName"
        required
        error={errors?.display_name}
      >
        <Input
          id="mail-displayName"
          type="text"
          placeholder="Blue Cross Blue Shield"
          value={value.display_name || ''}
          onChange={(e) => onChange({ ...value, display_name: e.target.value })}
          disabled={disabled}
          className={cn(inputStyles, errors?.display_name && inputErrorStyles)}
        />
      </FormField>

      {/* Address Section */}
      <div className="space-y-2">
        <FormField
          label="Street"
          htmlFor="mail-addressLine1"
          required
          error={errors?.address_line1}
        >
          <AddressAutocomplete
            id="mail-addressLine1"
            value={address.address_line1 || ''}
            onChange={(v) => handleAddressField('address_line1', v)}
            onAddressSelect={handleAddressAutocomplete}
            placeholder="Start typing address..."
            disabled={disabled}
            inputClassName={cn(
              inputStyles,
              errors?.address_line1 && inputErrorStyles,
            )}
          />
        </FormField>

        <FormField
          label="Line 2"
          htmlFor="mail-addressLine2"
          error={errors?.address_line2}
        >
          <Input
            id="mail-addressLine2"
            type="text"
            placeholder="Suite, Unit"
            value={address.address_line2 || ''}
            onChange={(e) =>
              handleAddressField('address_line2', e.target.value)
            }
            disabled={disabled}
            className={cn(
              inputStyles,
              errors?.address_line2 && inputErrorStyles,
            )}
          />
        </FormField>

        <div className="grid grid-cols-5 gap-2">
          <FormField
            label="City"
            htmlFor="mail-city"
            required
            error={errors?.city}
            className="col-span-2"
          >
            <Input
              id="mail-city"
              type="text"
              placeholder="City"
              value={address.city || ''}
              onChange={(e) => handleAddressField('city', e.target.value)}
              disabled={disabled}
              className={cn(inputStyles, errors?.city && inputErrorStyles)}
            />
          </FormField>

          <FormField
            label="State"
            htmlFor="mail-state"
            required
            error={errors?.state}
            className="col-span-2"
          >
            <StateCodeSelect
              id="mail-state"
              value={address.state || ''}
              onChange={(code) => handleAddressField('state', code)}
              disabled={disabled}
              className={cn(inputStyles, errors?.state && inputErrorStyles)}
              ariaLabel="State"
            />
          </FormField>

          <FormField
            label="ZIP"
            htmlFor="mail-zipCode"
            required
            error={errors?.zip_code}
          >
            <Input
              id="mail-zipCode"
              type="text"
              placeholder="ZIP"
              value={address.zip_code || ''}
              onChange={(e) => handleAddressField('zip_code', e.target.value)}
              disabled={disabled}
              className={cn(inputStyles, errors?.zip_code && inputErrorStyles)}
            />
          </FormField>
        </div>
      </div>

      {/* Mailing Class Selector */}
      <div className="space-y-1">
        <span className={labelStyles}>Delivery</span>
        <div className="flex gap-1.5">
          {MAILING_OPTIONS.map(
            ({ value: optValue, label, description, disabled: optDisabled }) => {
              const isSelected = selectedClass === optValue;
              const isDisabled = disabled || optDisabled;
              return (
                <button
                  key={optValue}
                  type="button"
                  onClick={() =>
                    !optDisabled && onChange({ ...value, mailing_class: optValue })
                  }
                  disabled={isDisabled}
                  className={cn(
                    'flex-1 rounded-md px-2 py-1.5 text-left transition-colors border',
                    isSelected
                      ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                      : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:border-indigo-300 hover:bg-indigo-50/40',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                  )}
                  aria-pressed={isSelected}
                >
                  <p className="text-[11px] font-medium">{label}</p>
                  <p className="text-[9px] text-zinc-500">{description}</p>
                </button>
              );
            },
          )}
        </div>
      </div>
    </div>
  );
};
